/**
 * Formatting and date-range helpers for the Meta Ads / Google Ads dashboards.
 * Money is shown in the ad account's own currency (AdsReport.currency).
 */

export function money(n: number | null | undefined, currency: string | null, decimals?: number): string {
  if (n === null || n === undefined || !isFinite(n)) return '—';
  const d = decimals ?? (Math.abs(n) >= 1000 ? 0 : 2);
  try {
    return n.toLocaleString(undefined, { style: 'currency', currency: currency || 'USD', minimumFractionDigits: d, maximumFractionDigits: d });
  } catch {
    return `${currency ?? ''} ${n.toFixed(d)}`.trim();
  }
}

export function num(n: number | null | undefined, decimals = 0): string {
  if (n === null || n === undefined || !isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** 12,345 → 12.3K for chart axes. */
export function compact(n: number): string {
  return Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

export function pct(n: number | null | undefined): string {
  return n === null || n === undefined || !isFinite(n) ? '—' : `${n.toFixed(2)}%`;
}

/**
 * Change against the previous period. `lowerIsBetter` flips the colour for
 * cost metrics (CPC, CPA, CPM): spending less per click is good news.
 */
export function delta(
  current: number | null | undefined,
  previous: number | null | undefined,
  lowerIsBetter = false,
): { trend: 'positive' | 'negative' | 'neutral'; trendLabel: string } | null {
  if (current === null || current === undefined || previous === null || previous === undefined) return null;
  if (previous === 0) return current === 0 ? { trend: 'neutral', trendLabel: '0%' } : null;
  const change = ((current - previous) / Math.abs(previous)) * 100;
  if (Math.abs(change) < 0.5) return { trend: 'neutral', trendLabel: '0%' };
  const good = lowerIsBetter ? change < 0 : change > 0;
  return { trend: good ? 'positive' : 'negative', trendLabel: `${change > 0 ? '+' : ''}${change.toFixed(change >= 100 || change <= -100 ? 0 : 1)}%` };
}

// ─── Date ranges ────────────────────────────────────────────────────────────

export type RangeKey = '7d' | '14d' | '30d' | '90d' | 'thismonth' | 'lastmonth' | 'ytd' | '12m' | 'custom';

export const RANGE_OPTIONS: { value: RangeKey; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '14d', label: 'Last 14 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'thismonth', label: 'This month' },
  { value: 'lastmonth', label: 'Last month' },
  { value: 'ytd', label: 'Year to date' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'custom', label: 'Custom range' },
];

export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function fromIso(s: string | null | undefined): Date | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
}

/** Local-calendar range for a preset, ending today. */
export function resolveRange(key: Exclude<RangeKey, 'custom'>, now = new Date()): { start: string; end: string } {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const back = (days: number) => new Date(today.getFullYear(), today.getMonth(), today.getDate() - (days - 1));
  switch (key) {
    case '7d': return { start: isoDate(back(7)), end: isoDate(today) };
    case '14d': return { start: isoDate(back(14)), end: isoDate(today) };
    case '30d': return { start: isoDate(back(30)), end: isoDate(today) };
    case '90d': return { start: isoDate(back(90)), end: isoDate(today) };
    case 'thismonth': return { start: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)), end: isoDate(today) };
    case 'lastmonth':
      return {
        start: isoDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        end: isoDate(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    case 'ytd': return { start: isoDate(new Date(today.getFullYear(), 0, 1)), end: isoDate(today) };
    case '12m': return { start: isoDate(new Date(today.getFullYear() - 1, today.getMonth(), today.getDate() + 1)), end: isoDate(today) };
  }
}

/** "Oct 3" / "Oct 3, 2025" when the range spans years. */
export function shortDate(iso: string, withYear = false): string {
  const d = fromIso(iso);
  if (!d) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}) });
}

/** Long ranges are charted by week or month so the bars stay readable. */
export type Bucket = 'day' | 'week' | 'month';
export function bucketFor(days: number): Bucket {
  if (days > 180) return 'month';
  if (days > 62) return 'week';
  return 'day';
}

export function bucketKey(iso: string, bucket: Bucket): string {
  if (bucket === 'day') return iso;
  const d = fromIso(iso)!;
  if (bucket === 'month') return isoDate(new Date(d.getFullYear(), d.getMonth(), 1));
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7));
  return isoDate(monday);
}
